import React, { FC, useContext, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { FACTORS_TO_SHOW } from "@app/service/user";
import { Button } from "@natera/form";
import {
  ConfigContext,
  ErrorContext,
  IntlContext,
  MfaErrorContext,
  MfaSetupContext,
  NotificationContext,
  ProfileContext,
} from "@app/provider";
import { verifyCodeLength } from "@app/utils";
import { Link } from "@natera/navigation";
import Svg from "@natera/material/lib/svg";
import Logo from "@assets/svg/natera-portal-logo.svg";
import { CodeInput, SpinnerView, GradientWrapper } from "@app/components";
import { useVerifyCodeForm } from "@app/hooks";

import "./verifyCodeForm.scss";
import { trimTrailingSlashes } from "@app/utils/common";
import { useLocation } from "react-router-dom";
import { routes } from "@app/routing";

export const messages = defineMessages({
  verifyCodeFormSentTo: {
    id: "verifyCodeFormSentTo",
    defaultMessage: "Enter your code sent to {target}",
  },
  verifyCodeFormArrivalSubtitle: {
    id: "verifyCodeFormArrivalSubtitle",
    defaultMessage: "It may take a few minutes to arrive.",
  },
  verifyCodeFormInputLabel: {
    id: "verifyCodeFormInputLabel",
    defaultMessage: "Verification Code",
  },
  verifyCodeFormResendButton: {
    id: "verifyCodeFormResendButton",
    defaultMessage: "Resend Code",
  },
  verifyCodeFormUpdateMobileNumber: {
    id: "verifyCodeFormUpdateMobileNumber",
    defaultMessage: "Update Mobile Number",
  },
  verifyCodeFormResendIn: {
    id: "verifyCodeFormResendIn",
    defaultMessage: "Resend in {timeLeft}s",
  },
  verifyCodeFormVerifyButton: {
    id: "verifyCodeFormVerifyButton",
    defaultMessage: "Verify",
  },
  verifyCodeFormGoBackButton: {
    id: "verifyCodeFormGoBackButton",
    defaultMessage: "Back",
  },
});

interface VerifyCodeFormProps extends React.HTMLAttributes<HTMLDivElement> {
  type: FACTORS_TO_SHOW;
  profile?: {
    email?: string;
    phoneNumber?: string;
  };
  handleCode: (code: string) => void;
  handleResend: () => void;
  goBack?: () => void;
}

const VerifyCodeForm: FC<VerifyCodeFormProps> = ({
  type,
  profile,
  handleCode,
  handleResend,
  goBack,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const pathname = location.pathname;
  const { config } = useContext(ConfigContext);
  const { notifications } = useContext(NotificationContext);
  const { isLoading: profileIsLoading } = useContext(ProfileContext);

  const { retranslateErrors } = useContext(MfaErrorContext);

  const { isLoading: mfaIsLoading } = useContext(MfaSetupContext);

  const { profileDataIsLoading, updatePatientIsLoading } =
    useContext(ProfileContext);

  const errorController = useContext(ErrorContext);
  const { getValidationError, clearValidationError } = errorController;
  const { currentLanguage } = useContext(IntlContext);

  React.useEffect(() => {
    retranslateErrors(errorController);
  }, [currentLanguage]);

  const {
    timeLeft,
    updateMobileNumberDialog,
    handleResendCodeClick,
    handleUpdateMobileNumberClick,
  } = useVerifyCodeForm({
    defaultCounterTime: config.DEFAULT_COUNTER_TIME,
    defaultTimeLeft: config.DEFAULT_TIME_LEFT,
    validationErrorKey: "code",
    profile,
  });

  const isDisplayGradientWrapper = [
    routes.logIn,
    routes.setupProfile,
    routes.confirmation,
  ]
    .map(trimTrailingSlashes)
    .includes(pathname);

  const handleCodeSubmit = (code: string) => {
    clearValidationError("code");
    handleCode(code);
  };

  const handleCodeChange = () => {
    clearValidationError("code");
  };

  const onResendCodeClick = () => {
    handleResendCodeClick(handleResend);
  };

  const displayCodeVerificationTarget = () => {
    return type === FACTORS_TO_SHOW.email
      ? profile?.email
      : profile?.phoneNumber;
  };

  const goBackClicked = () => {
    clearValidationError("code");
    goBack?.();
  };

  const isLoading = useMemo(
    () =>
      profileIsLoading ||
      mfaIsLoading ||
      profileDataIsLoading ||
      updatePatientIsLoading,
    [
      profileIsLoading,
      mfaIsLoading,
      profileDataIsLoading,
      updatePatientIsLoading,
    ]
  );

  return (
    <div className="verify-code-container">
      <GradientWrapper display={isDisplayGradientWrapper}>
        <div className="verify-code-logo">
          <Link to="/">
            <Svg content={Logo} />
          </Link>
        </div>
        {notifications}
        <div className="verify-code-content">
          <h3>
            {intl.formatMessage(messages.verifyCodeFormSentTo, {
              target: displayCodeVerificationTarget(),
            })}
          </h3>

          <div className="verify-code-to">
            <span>
              {intl.formatMessage(messages.verifyCodeFormArrivalSubtitle)}
            </span>
          </div>

          <CodeInput
            onCodeComplete={handleCodeSubmit}
            onCodeChange={handleCodeChange}
            hasError={!!getValidationError("code")}
            errorMessage={getValidationError("code")}
            codeFieldLength={verifyCodeLength(type)}
          />

          <div className="verify-code-buttons-container">
            {timeLeft === 0 ? (
              <>
                <Button unelevated type="button" onClick={onResendCodeClick}>
                  {intl.formatMessage(messages.verifyCodeFormResendButton)}
                </Button>
              </>
            ) : (
              <Button unelevated type="button" disabled>
                {intl.formatMessage(messages.verifyCodeFormResendIn, {
                  timeLeft,
                })}
              </Button>
            )}

            {(type === FACTORS_TO_SHOW.sms ||
              type === FACTORS_TO_SHOW.call) && (
              <Button
                className="update-mobile-number-button"
                outlined
                type="button"
                onClick={handleUpdateMobileNumberClick}
              >
                {intl.formatMessage(messages.verifyCodeFormUpdateMobileNumber)}
              </Button>
            )}
            {goBack && (
              <Button
                className="go-back-button"
                type="button"
                onClick={goBackClicked}
              >
                {intl.formatMessage(messages.verifyCodeFormGoBackButton)}
              </Button>
            )}
          </div>
        </div>
        {updateMobileNumberDialog.getDialog()}
        <SpinnerView isLoading={isLoading} />
      </GradientWrapper>
    </div>
  );
};

export default VerifyCodeForm;
